import { Plus } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';

import { Flex } from '@/components/base';
import Button from '@/components/Button/Button';
import Modal from '@/components/Modal/Modal';
import { MainText } from '@/components/Typography/NamedTypo';
import Typo from '@/components/Typography/Typo';
import useColor from '@/hooks/domain/useColor';
import useCrossWindowState from '@/hooks/domain/useCrossWindowState';
import useWorkspaceStore from '@/stores/WorkspaceStore';
import { SOURCE_TYPE } from '@/types/feedbackConst';
import { getSlackUrl } from '@/utils/integration';

import { ProviderContainer } from '../../components/ProviderContainer';
import type { SlackResponse } from '../../integrationRedirection/slack';
import type { IntegrationItemProps } from '../../types';
import SlackIntegrationInfoModal from './SlackIntegrationInfoModal';
import SlackIntegrationConfigModal from './SlackWorkspaceConfigModal';
import { useSlack } from './useSlack';

export const SlackItem = ({ isConnector }: IntegrationItemProps) => {
  const color = useColor();
  const currWorkspace = useWorkspaceStore((state) => state.currWorkspace);

  const { slackWorkspaces, disconnectSlackMutation, invalidateSlackQuery } =
    useSlack(!isConnector);

  const [viewInfo, setViewInfo] = useState(false);
  const [viewConfigId, setViewConfigId] = useState<string | null>(null);

  const [redirect, setRedirect, removeKey] =
    useCrossWindowState<SlackResponse | null>('slack-redirect', null);

  const selectedWorkspace = useMemo(
    () => slackWorkspaces.find((workspace) => workspace.id === viewConfigId),
    [slackWorkspaces, viewConfigId],
  );

  const handleConnect = () => {
    if (!currWorkspace) return;
    window.open(getSlackUrl(currWorkspace.alias));
  };

  const handleDisconnect = (teamId: string) => {
    disconnectSlackMutation(teamId);
  };

  useEffect(() => {
    if (isConnector) return;
    if (!currWorkspace) return;
    if (!redirect) return;

    setRedirect(null);
    removeKey();
    invalidateSlackQuery();
    setViewConfigId(redirect.team_id);
  }, [
    currWorkspace,
    invalidateSlackQuery,
    isConnector,
    redirect,
    removeKey,
    setRedirect,
  ]);

  return (
    <>
      <ProviderContainer
        source={SOURCE_TYPE.SLACK}
        connected={slackWorkspaces.length > 0}
        multiInfoEnabled={true}
        infos={slackWorkspaces}
        description="Collect feedback and ideas to Syncly."
        connectedDisplay={(info) => info.name}
        onConnectClick={handleConnect}
        onAddInfoClick={handleConnect}
        onDisconnect={(info) => handleDisconnect(info.id)}
        additionalActions={(info) =>
          info.not_found ? (
            <Typo type="label3" color={color.red600}>
              Workspace not found
            </Typo>
          ) : (
            <Button variant="outlined" onClick={() => setViewConfigId(info.id)}>
              Manage Channel
            </Button>
          )
        }
      />

      {slackWorkspaces.length > 0 && (
        <Flex j="flex-start" g={4} a="center" style={{ padding: '8px 4px' }}>
          <Button
            leftIcon={<Plus />}
            variant="ghost_secondary"
            onClick={handleConnect}
          >
            Add more Slack integration
          </Button>
          <MainText dim>|</MainText>
          <Button
            variant="ghost"
            onClick={() => setViewInfo(true)}
            style={{ color: color.gray600 }}
          >
            Download emoji
          </Button>
          <MainText dim>|</MainText>
          <Button
            variant="ghost"
            onClick={() =>
              window.open(import.meta.env.VITE_PUBLIC_ADD_EMOJI_TO_SLACK_URL, '_blank')
            }
            style={{ color: color.gray600 }}
          >
            How to add emoji to slack
          </Button>
        </Flex>
      )}

      <Modal status={viewInfo} closeModal={() => setViewInfo(false)} useBackgroundClose>
        <SlackIntegrationInfoModal />
      </Modal>

      <Modal
        status={!!viewConfigId}
        closeModal={() => setViewConfigId(null)}
        useBackgroundClose
      >
        <SlackIntegrationConfigModal workspace={selectedWorkspace} />
      </Modal>
    </>
  );
};
